import { useCallback, useEffect, useState } from "react";

import { SelectData } from "../../../../models";
import { searchProjects, SearchProjectsResponse } from "../../../../service/query";
import { Status } from "../../../../service/Shared";

interface UseLinkProjectModalProps {
  show: boolean;
}

interface UseLinkProjectModalReturn {
  projectOptions: SelectData;
  projectUuid: string | null;
  selectedProject: SearchProjectsResponse["results"][number] | null;
  companyRole: string;
  setCompanyRole: (value: string) => void;
  setProjectUuid: (value: string) => void;
  fetchProjects: (value: string) => void;
  reset: () => void;
}

export const useLinkProjectModal = ({ show }: UseLinkProjectModalProps): UseLinkProjectModalReturn => {
  const [projectOptions, setProjectOptions] = useState<SelectData>([]);
  const [projectLookup, setProjectLookup] = useState<Record<string, SearchProjectsResponse["results"][number]>>({});
  const [projectUuid, setProjectUuid] = useState<string | null>(null);
  const [companyRole, setCompanyRole] = useState<string>("");

  const fetchProjects = useCallback(async (searchTerm: string) => {
    const response = await searchProjects({
      paging: {
        afterCursor: null,
        beforeCursor: null,
        limit: 25,
      },
      sort: [{ key: "results.displayName", direction: "asc" }],
      filter: searchTerm
        ? {
            results: {
              displayName: {
                operator: "contains",
                value: searchTerm,
              },
            },
          }
        : undefined,
    });

    if (response.status === Status.Success && response.data?.results) {
      const nextLookup: Record<string, SearchProjectsResponse["results"][number]> = {};
      const nextOptions = response.data.results.map((project) => {
        nextLookup[project.uuid] = project;
        return {
          key: project.uuid,
          value: project.displayName,
        };
      });
      setProjectLookup(nextLookup);
      setProjectOptions(nextOptions);
      return;
    }

    setProjectLookup({});
    setProjectOptions([]);
  }, []);

  const reset = (): void => {
    setProjectOptions([]);
    setProjectLookup({});
    setProjectUuid(null);
    setCompanyRole("");
  };

  useEffect(() => {
    if (show) {
      fetchProjects("");
    } else {
      reset();
    }
  }, [show, fetchProjects]);

  return {
    projectOptions,
    projectUuid,
    selectedProject: projectUuid ? (projectLookup[projectUuid] ?? null) : null,
    companyRole,
    setCompanyRole,
    setProjectUuid,
    fetchProjects,
    reset,
  };
};
